import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import useUserHistory from 'hooks/useUserHistory';
import UserHistoryEvent, { UserHistoryEventType } from 'types/UserHistoryEvent';

interface UserHistoryPanelProps {
  email: string;
}

const EVENT_STYLES: Record<
  UserHistoryEventType,
  { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' | 'primary' }
> = {
  INITIAL_REQUEST_PENDING: { label: 'Initial Request — Pending', color: 'info' },
  INITIAL_REQUEST_APPROVED: { label: 'Initial Request — Approved', color: 'success' },
  INITIAL_REQUEST_DENIED: { label: 'Initial Request — Denied', color: 'error' },
  UPGRADE_REQUEST_PENDING: { label: 'Upgrade Request — Pending', color: 'info' },
  UPGRADE_APPROVED: { label: 'Upgrade — Approved', color: 'success' },
  UPGRADE_DENIED: { label: 'Upgrade — Denied', color: 'error' },
  ACCOUNT_ENABLED: { label: 'Account Enabled', color: 'success' },
  ACCOUNT_DISABLED: { label: 'Account Disabled', color: 'error' },
  ADMIN_TIER_UPDATED: { label: 'Admin Tier Updated', color: 'primary' },
  ADMIN_COMMENT_UPDATED: { label: 'Admin Comment Updated', color: 'default' },
};

const formatTs = (ts?: string): string => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const renderDetails = (evt: UserHistoryEvent): React.ReactNode => {
  const d = evt.details;
  if (!d) return null;

  switch (evt.event) {
    case 'UPGRADE_REQUEST_PENDING':
      return (
        <Box sx={{ mt: 0.5 }}>
          {d.requested_tier != null && (
            <Typography variant="body2">
              <strong>Requested tier:</strong> {String(d.requested_tier)}
            </Typography>
          )}
          {d.orcid_id != null && (
            <Typography variant="body2">
              <strong>ORCID iD:</strong> {String(d.orcid_id)}
            </Typography>
          )}
        </Box>
      );
    case 'UPGRADE_APPROVED':
    case 'ADMIN_TIER_UPDATED': {
      const prev = d.previous_tier ? String(d.previous_tier) : null;
      const next = d.new_tier ? String(d.new_tier) : null;
      const requested = d.requested_tier ? String(d.requested_tier) : null;
      const showRequestedNote = requested && next && requested !== next;
      if (!prev && !next) return null;
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="body2">
            {prev && <strong>{prev}</strong>}
            {prev && next && ' → '}
            {next && <strong>{next}</strong>}
          </Typography>
          {showRequestedNote && (
            <Typography variant="body2" color="text.secondary">
              (User requested: {requested})
            </Typography>
          )}
        </Box>
      );
    }
    case 'UPGRADE_DENIED': {
      const requested = d.requested_tier ? String(d.requested_tier) : null;
      if (!requested) return null;
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Requested: {requested}
        </Typography>
      );
    }
    case 'ADMIN_COMMENT_UPDATED':
      return null;
    case 'ACCOUNT_DISABLED':
    case 'ACCOUNT_ENABLED':
    case 'INITIAL_REQUEST_APPROVED':
    case 'INITIAL_REQUEST_DENIED':
      return null;
    default:
      return null;
  }
};

const UserHistoryPanel: React.FC<UserHistoryPanelProps> = ({ email }) => {
  const { history, isLoading, error, fetchHistory } = useUserHistory();

  useEffect(() => {
    if (email) fetchHistory(email);
  }, [email, fetchHistory]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        User History
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading history…
          </Typography>
        </Box>
      )}

      {!isLoading && error && (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      )}

      {!isLoading && !error && history.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No history available for this user.
        </Typography>
      )}

      {!isLoading && !error && history.length > 0 && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 1,
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {history.map((evt, idx) => {
            const style = EVENT_STYLES[evt.event] ?? {
              label: evt.event,
              color: 'default' as const,
            };
            return (
              <Box
                key={evt.event_key ?? `${evt.event_ts}-${idx}`}
                sx={{ p: 1.25 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={style.label} size="small" color={style.color} variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {formatTs(evt.event_ts)}
                  </Typography>
                  {evt.performed_by && (
                    <Typography variant="caption" color="text.secondary">
                      · by {evt.performed_by}
                    </Typography>
                  )}
                  {evt.current_user_tier &&
                    [
                      'INITIAL_REQUEST_APPROVED',
                      'ADMIN_TIER_UPDATED',
                      'UPGRADE_APPROVED',
                      'UPGRADE_DENIED',
                    ].includes(evt.event) && (
                    <Typography variant="caption" color="text.secondary">
                      · Assigned: {evt.current_user_tier}
                    </Typography>
                  )}
                </Box>

                {evt.comments && (
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {evt.comments}
                  </Typography>
                )}

                {renderDetails(evt)}

                {idx < history.length - 1 && <Divider sx={{ mt: 1.25 }} />}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default UserHistoryPanel;